import {
  Button,
  Flex,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  useReactTable,
  VisibilityTableState,
  ColumnOrderTableState,
  GroupingTableState,
  ColumnSizingTableState,
  ColumnPinningTableState,
  SortingTableState,
  ExpandedTableState,
  PaginationTableState,
  RowSelectionTableState,
} from '@tanstack/react-table';
import React, { useState, useMemo, useRef } from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Accounting, Bank, Util } from '@finley-technologies/finley-types';

import { TAccountTypeGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { colors } from '~/packages/common-frontend/colors';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { TanStackPaginationControl } from '~/packages/common-frontend/components/TanStack';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { tableHeaderStyle } from '~/packages/common-frontend/styles';
import {
  formatCurrencyAmount,
  getTAccountTypeLabel,
} from '~/packages/common-frontend/utils';
import {
  CohortCadence,
  cohortCadenceLabelFormatter,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';
import { TransactionsFilterState } from '~/packages/transactions/hooks/transactionsHooks';
import { useGetDailyAccountActivityQuery } from '~/packages/transactions/requests/transactions.gql.generated';

// types
type DateToTAccountsDataMapType = Record<
  string,
  {
    total: number;
    date: string; // this is more like a (date)label so we don't recreate as many arrays
  } & Partial<Record<TAccountTypeGQLType, number>>
>;

type TanstackTableState = Partial<
  VisibilityTableState &
    ColumnOrderTableState &
    ColumnPinningTableState &
    SortingTableState &
    ExpandedTableState &
    GroupingTableState &
    ColumnSizingTableState &
    PaginationTableState &
    RowSelectionTableState
>;

// helper functions
const getGroupingKey = (
  selectedFrequencyPeriod: Util.FrequencyPeriodType,
  dateStr: Util.FullDateStr,
) => {
  switch (selectedFrequencyPeriod) {
    case 'day':
      return commonDate.fullDateStrToHumanDateString(dateStr);
    case 'week':
    case 'month':
    case 'quarter':
    case 'year':
      return cohortCadenceLabelFormatter(
        selectedFrequencyPeriod as CohortCadence, // CohortCadence is all cadences but 'day'
        dateStr,
      );
    default:
      return finleyAssertUnreachable(
        selectedFrequencyPeriod as never,
        'selected frequency period is not in expected format',
      );
  }
};

const TABLE_COLUMN_CONFIGS: ColumnDef<
  { date: string; total: number } & Partial<
    Record<TAccountTypeGQLType, number>
  >,
  any
>[] = [
  {
    id: 'date',
    accessorKey: 'date',
    header: 'Date',
    meta: {
      style: {
        width: '14rem',
      },
    },
  },
  {
    id: 'total',
    accessorKey: 'total',
    header: 'Totals',
    cell: function formatAmount({ getValue }) {
      return (
        <Text>
          {typeof getValue() === 'number'
            ? formatCurrencyAmount(getValue(), Bank.Currency.USD)
            : '-'}
        </Text>
      );
    },
    meta: {
      style: {
        borderRight: '2px solid #e2e8f0',
        textAlign: 'right',
        width: '14rem',
      },
    },
  },
  {
    id: 'longTermDebt',
    accessorKey: 'longTermDebt',
    header: 'Principal',
    cell: function formatAmount({ getValue }) {
      return (
        <Text>
          {typeof getValue() === 'number'
            ? formatCurrencyAmount(getValue(), Bank.Currency.USD)
            : '-'}
        </Text>
      );
    },
  },
  ...Accounting.TAccountIterable.filter(
    (tAccountType) => tAccountType !== 'longTermDebt',
  ).map((tAccountType) => ({
    id: String(tAccountType),
    accessorKey: String(tAccountType),
    header: getTAccountTypeLabel(tAccountType),
    cell: function formatAmount({ getValue }: { getValue: () => number }) {
      return (
        <Text>
          {typeof getValue() === 'number'
            ? formatCurrencyAmount(getValue(), Bank.Currency.USD)
            : '-'}
        </Text>
      );
    },
  })),
];

const groupingKeyCache: Record<string, string> = {}; // date to to its formatted 'groupingkey'

const getCachedGroupingKey = ({
  selectedFrequencyPeriod,
  postingDate,
}: {
  selectedFrequencyPeriod: Util.FrequencyPeriodType;
  postingDate: Util.FullDateStr;
}) => {
  const cacheKey = `${selectedFrequencyPeriod}-${postingDate}`;

  if (groupingKeyCache[cacheKey]) return groupingKeyCache[cacheKey];

  const groupingKey = getGroupingKey(selectedFrequencyPeriod, postingDate);
  groupingKeyCache[cacheKey] = groupingKey;

  return groupingKey;
};

export const TransactionsDailyAccountTotalsTable = ({
  creditFacilityIds,
  loanIds,
  accountTypes,
  startDate,
  endDate,
  showBackToTopButton = true,
  initialTanstackTableState = {
    pagination: {
      pageSize: 100,
      pageIndex: 0,
    },
  },
  hasActiveFilters,
}: TransactionsFilterState & {
  showBackToTopButton?: boolean;
  initialTanstackTableState?: TanstackTableState;
  hasActiveFilters?: boolean;
}) => {
  const [
    selectedFrequencyPeriod,
    setSelectedFrequencyPeriod,
  ] = useState<Util.FrequencyPeriodType>('day');

  const {
    error,
    data: dailyAccountActivityQueryResult,
    loading,
  } = useGetDailyAccountActivityQuery({
    variables: {
      creditFacilityIds,
      loanIds,
      accountTypes,
      startDate,
      endDate,
    },
    fetchPolicy: 'no-cache',
  });

  // TODO: @jjss886 & @markhaghani, shift data transformation to BE
  const { dailyAccountData, columnVisibility } = useMemo(() => {
    if (!dailyAccountActivityQueryResult?.activity) {
      return { dailyAccountData: [], columnVisibility: {} };
    }

    const columnVisibilityRecord: Record<string, boolean> = {};
    Accounting.TAccountIterable.forEach((tAccount) => {
      columnVisibilityRecord[tAccount] = false;
    });

    const dateToTAccountsData: DateToTAccountsDataMapType = {};

    dailyAccountActivityQueryResult.activity.accountData.forEach(
      (dailyAccountEntry) => {
        const { postingDate, aggregateAmount, tAccount } = dailyAccountEntry;

        const groupingKey = getCachedGroupingKey({
          selectedFrequencyPeriod,
          postingDate,
        });
        columnVisibilityRecord[tAccount] = true;

        if (!dateToTAccountsData[groupingKey]) {
          dateToTAccountsData[groupingKey] = {
            total: 0,
            date: groupingKey,
          };
        }
        const tAccountsData = dateToTAccountsData[groupingKey];

        tAccountsData[tAccount] = commonUtils.jsRound({
          value: (tAccountsData[tAccount] || 0) + aggregateAmount,
          precision: 4,
        });

        tAccountsData.total = commonUtils.jsRound({
          value: tAccountsData.total + aggregateAmount,
          precision: 4,
        });
      },
    );

    return {
      dailyAccountData:
        selectedFrequencyPeriod === 'year'
          ? Object.values(dateToTAccountsData).reverse()
          : Object.values(dateToTAccountsData),
      columnVisibility: columnVisibilityRecord,
    };
  }, [dailyAccountActivityQueryResult, selectedFrequencyPeriod]);

  const tableRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data: dailyAccountData,
    columns: TABLE_COLUMN_CONFIGS,
    state: { columnVisibility },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      columnVisibility,
      ...initialTanstackTableState,
    },
  });

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  if (error) throw error;
  if (loading) return <Skeleton height="500px" w="100%" />;
  if (dailyAccountData.length === 0) {
    return (
      <Flex py="2rem">
        <Typography>
          {hasActiveFilters
            ? 'No transactions were found for the selected filters. Adjust your selection.'
            : 'No transactions were found.'}
        </Typography>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        sx={{
          justifyContent: 'space-between',
          py: '1.5rem',
        }}
        ref={tableRef}
      >
        <Flex gap="2">
          {Util.FrequencyPeriodIterable.map((frequencyPeriod) => {
            const isSelected = frequencyPeriod === selectedFrequencyPeriod;
            return (
              <Button
                key={frequencyPeriod}
                onClick={() => setSelectedFrequencyPeriod(frequencyPeriod)}
                colorScheme="gray"
                variant="primary-muted"
                isActive={isSelected}
              >
                {frequencyPeriod.charAt(0).toUpperCase()}
              </Button>
            );
          })}
        </Flex>
        <CsvXlsxDownloadSelect
          dataRows={csvDownload.dataRows}
          headers={csvDownload.headers}
          filenamePrefix="transaction-account-activities"
        />
      </Flex>
      <TableContainer>
        <Table borderColor={colors.gray[100]} borderWidth="thin">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    sx={{
                      ...tableHeaderStyle,
                      ...header.column.columnDef.meta?.style,
                    }}
                  >
                    {flexRender(
                      String(header.column.columnDef.header),
                      header.getContext(),
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    sx={{
                      backgroundColor: 'white',
                      px: '1rem',
                      color: colors.black[70],
                      minWidth: '12rem',
                      maxWidth: cell.column.columnDef.meta?.maxWidth || 'auto',
                      whiteSpace: 'normal',
                      ...cell.column.columnDef.meta?.style,
                    }}
                    // TODO: @markhaghani add overflowX
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <TanStackPaginationControl table={table} />
      {showBackToTopButton && (
        <Flex justifyContent="right" mt="4">
          <Button
            variant="link"
            onClick={() =>
              tableRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
              })
            }
          >
            Back to the top
          </Button>
        </Flex>
      )}
    </>
  );
};
